import { useCallback, useMemo, useState } from 'react';

import { useEventInfoWithPlayersQuery } from '@/api/events/query.generated';
import { useSinglePlayerProfile } from '@/feature/fantasy-event-sheets/SinglePlayerProfileProvider';
import { useMatchPreview } from '@/feature/fantasy-event-sheets/hooks/use-match-preview';
import { useMatchPreviewEnabled } from '@/feature/fantasy-event-sheets/hooks/use-match-preview-enabled';
import { useGQLRequestPolicy } from '@/feature/guest-user/hooks/use-gql-request-policy';
import { useLeagueContentRenderer } from '@/feature/lobby/hooks/use-league-content-render';
import { useSocialFeedAndEventsData } from '@/feature/social-feed/use-social-feed';
import { useGetOrderedLeagues } from '@/hooks/use-fantasy-league-configs';
import { useResumeEffectWithDelay } from '@/hooks/use-resume';
import { League } from '@/types/api.generated';

import { FILTER_MODES_TABS, FilterType, Filters, getPositionFilters } from '../utils/filters';
import { useSortedListOfPlayers } from './use-sorted-lobby-players';

/**
 * Hook to compute the data to be displayed on the event details screen based on the selected event.
 *
 * This hook will fetch the required event data.
 * It will also return the render functions to render the data.
 */
export const useFantasyEventContent = ({
    league,
    eventId,
    isActive,
}: {
    league: League;
    eventId: string;
    isActive: boolean;
}) => {
    const [filterType, setFilterMode] = useState<FilterType>(FILTER_MODES_TABS[0].filterType);
    const requestPolicy = useGQLRequestPolicy('cache-and-network');

    const [{ data: eventWithPlayers, fetching: fetchingEvent }, fetchEventData] = useEventInfoWithPlayersQuery({
        variables: { id: eventId ?? '' },
        requestPolicy,
        pause: true,
    });

    const hasStaleEventsData =
        eventWithPlayers?.getEventByIdV2 !== undefined && eventWithPlayers.getEventByIdV2.id !== eventId;
    const eventLoading = fetchingEvent || eventWithPlayers === undefined || hasStaleEventsData;

    // don't use here useLiveEventsPropsUpdate since we already subscribe to event updates in FantasyEventSheets

    const eventsData = useMemo(() => {
        return hasStaleEventsData ? [] : [eventWithPlayers?.getEventByIdV2].filter(Boolean);
    }, [eventWithPlayers?.getEventByIdV2, hasStaleEventsData]);

    const { leagues } = useGetOrderedLeagues();
    const positionFilters = useMemo(() => getPositionFilters(league, leagues), [league, leagues]);

    const { sortedPlayers, popularProjectionsPlayers } = useSortedListOfPlayers({
        sortedEvents: eventsData,
        filterType,
        positionFilters,
    });

    // Fetch social feed lineups
    const { data: socialFeedEntries, fetching: fetchingSocialFeed } = useSocialFeedAndEventsData({
        enabled: filterType === Filters.SocialLineups && isActive,
        eventId,
    });

    // Fetch match preview
    const matchPreviewEnabled = useMatchPreviewEnabled();
    const { data: matchPreviewData, isLoading: matchPreviewLoading } = useMatchPreview({
        eventId: eventId,
        enabled: matchPreviewEnabled && filterType === Filters.Preview && isActive,
    });

    const refreshData = useCallback(() => {
        if (isActive) {
            fetchEventData();
        }
    }, [fetchEventData, isActive]);

    const { openSinglePlayerProfile } = useSinglePlayerProfile();
    useResumeEffectWithDelay(refreshData);

    const { renderPlayerItem, renderEventCards, renderFilters, renderLeagueFooter, renderSocialFeedItem } =
        useLeagueContentRenderer({
            league,
            selectedFilter: filterType,
            selectFilter: setFilterMode,
            sortedEvents: eventsData,
            sortedPlayers,
            positionFilters,
            socialFeedEntries,
            matchPreviewData,
            eventsLoading: eventLoading,
            socialLineupsLoading: fetchingSocialFeed,
            matchPreviewLoading,
            hideGameTime: true,
            openSinglePlayerProfile,
            popularProjectionsPlayers,
            showMatchPreviewTab: matchPreviewEnabled,
        });

    return {
        sortedPlayers,
        socialFeedEntries,
        matchPreviewData,
        filterType,
        renderPlayerItem,
        renderSocialFeedItem,
        renderEventCards,
        renderFilters,
        renderLeagueFooter,
    };
};
